import React, { Component } from 'react';
import { connect } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { DROPDOWN_CONFIG, DATATABLE_CONFIG, USER_SUBMENU_ID } from '../../../shared/Config';
import { HASACCESS } from '../../../shared/utility';
import ItemwiseSalesSummaryReportHeader from './ItemwiseSalesSummaryReportHeader';
import './ItemwiseSalesSummaryReport.scss';

// Dummy restaurants — replace with API/Redux data
const RESTAURANTS = [
    { label: 'Chenthoor Restaurant', value: 'Chenthoor Restaurant' },
    { label: 'Chenthoor Restaurant - 2', value: 'Chenthoor Restaurant - 2' },
];

// Dummy single-day item sales (from the POS item wise summary) — replace with API data: { name, qty, amount }
const SAMPLE_ITEMS = [
    ['APPLE JUICE', 2, 280], ['CHAPPATI + KURMA + RAITHA', 4, 160], ['CHILLY PAROTA', 4, 560], ['COFFEE', 2, 50],
    ['GHEE DOSA', 4, 480], ['GOBI 65', 4, 720], ['GOBI FRIED RICE', 1, 170], ['GREEN SALAD', 1, 80],
    ['IDLY', 59, 1475], ['LIME SODA (SWEET)', 1, 100], ['MEALS - LIMITED', 26, 3120], ['MEALS - UNLIMITED', 30, 4950],
    ['MIXED NOODULS', 1, 210], ['MUSHROOM 65', 2, 360], ['MUSHROOM FRIED RICE', 5, 900], ['ONION DOSA', 6, 750],
    ['PANEER 65', 1, 210], ['PANEER BUTTER MASALA', 1, 220], ['PANEER FRIED RICE', 1, 200], ['PLAIN DOSA', 6, 600],
    ['PODI DOSA', 3, 360], ['PONGAL', 6, 600], ['POORI & MASALA', 38, 1900], ['PORATTA (1) + KURMA', 8, 400],
    ['SOUP', 2, 180], ['UTHAPPAM', 6, 450], ['VEG FRIED RICE', 8, 1360], ['VEG NOODLES', 1, 150], ['WATER 1LTR', 7, 140],
];

const GST_RATE = 0.05;

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const pad = (n) => String(n).padStart(2, '0');

const formatDate = (d) => `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;

const formatNum = (v) => (v === null || v === undefined || v === '' ? '' : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const dayCount = (from, to) => {
    const a = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    const b = new Date(to.getFullYear(), to.getMonth(), to.getDate());
    return Math.round((b - a) / 86400000) + 1;
};

// Dummy aggregation over the date range — replace with API data
function buildRows(from, to) {
    const days = dayCount(from, to);
    return SAMPLE_ITEMS.map(([name, qty, amount], i) => ({
        id: i,
        name,
        qty: qty * days,
        amount: round2(amount * days),
    }));
}

const COLUMN_LABELS = ['S.NO', 'ITEM NAME', 'QTY', 'AMOUNT'];

class ItemwiseSalesSummaryReport extends Component {
    state = {
        Restaurant: RESTAURANTS[0].value,
        fromDate: new Date(),
        toDate: new Date(),
        tableData: [],
        searched: false,
        btnLoading: null, // key of the action currently running: search | showAll | clear | pdf | excel
    };

    componentDidMount() {
        this.getAccess = HASACCESS(USER_SUBMENU_ID);
    }

    componentWillUnmount() {
        this.unmounted = true;
    }

    // Same loader as the Daily Sales Report (full-page .page-loader overlay); further clicks ignored meanwhile.
    runWithLoader = (key, action) => {
        if (this.state.btnLoading) return;
        this.setState({ btnLoading: key }, () => {
            setTimeout(() => {
                try {
                    action();
                } catch (e) {
                    this.growl && this.growl.show({ severity: 'error', summary: 'Error', detail: (key === 'pdf' || key === 'excel') ? 'Export failed. Please try again.' : 'Unable to load the report. Please try again.' });
                } finally {
                    if (!this.unmounted) this.setState({ btnLoading: null });
                }
            }, 300);
        });
    };

    handleSearch = () => this.runWithLoader('search', this.handleSearchAction);
    handleShowAll = () => this.runWithLoader('showAll', this.handleShowAllAction);
    handleClear = () => this.runWithLoader('clear', this.handleClearAction);
    handleExportExcel = () => this.runWithLoader('excel', this.handleExportExcelAction);
    handleExportPdf = () => this.runWithLoader('pdf', this.handleExportPdfAction);

    handleSearchAction = () => {
        const { fromDate, toDate, Restaurant } = this.state;
        if (!Restaurant) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'Please select Restaurant' });
            return;
        }
        if (!fromDate || !toDate) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'Please select From Date and To Date' });
            return;
        }
        if (fromDate > toDate) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'From Date cannot be after To Date' });
            return;
        }
        this.setState({ tableData: buildRows(fromDate, toDate), searched: true });
    };

    handleShowAllAction = () => {
        const now = new Date();
        const from = new Date(now.getFullYear(), now.getMonth(), 1);
        this.setState({ fromDate: from, toDate: now, tableData: buildRows(from, now), searched: true });
    };

    handleClearAction = () => {
        this.setState({ Restaurant: RESTAURANTS[0].value, fromDate: new Date(), toDate: new Date(), tableData: [], searched: false });
    };

    getTotals = () => {
        const { tableData } = this.state;
        const qty = tableData.reduce((a, r) => a + Number(r.qty || 0), 0);
        const amount = round2(tableData.reduce((a, r) => a + Number(r.amount || 0), 0));
        const gst = round2(amount * GST_RATE);
        return { qty, amount, cgst: round2(gst / 2), sgst: round2(gst / 2) };
    };

    buildExportMatrix = () => {
        const { tableData } = this.state;
        const t = this.getTotals();
        const body = tableData.map((r, i) => [i + 1, r.name, r.qty, r.amount]);
        const footer = [
            ['', 'TOTAL', t.qty, t.amount],
            ['', 'CGST', '', t.cgst],
            ['', 'SGST', '', t.sgst],
            ['', 'NET AMOUNT', '', t.amount],
        ];
        return { body, footer };
    };

    reportTitle = () => {
        const { Restaurant, fromDate, toDate } = this.state;
        return `${String(Restaurant).toUpperCase()} - ITEMWISE SALES SUMMARY REPORT (${formatDate(fromDate)} to ${formatDate(toDate)})`;
    };

    handleExportExcelAction = () => {
        const { body, footer } = this.buildExportMatrix();
        if (body.length === 0) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'No data to export' });
            return;
        }
        const ws = XLSX.utils.aoa_to_sheet([[this.reportTitle()], COLUMN_LABELS, ...body, ...footer]);
        ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: COLUMN_LABELS.length - 1 } }];
        ws['!cols'] = [{ wch: 8 }, { wch: 34 }, { wch: 10 }, { wch: 16 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Itemwise Sales Summary');
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([buf], { type: 'application/octet-stream' }), 'Itemwise_Sales_Summary_Report.xlsx');
    };

    handleExportPdfAction = () => {
        const { body, footer } = this.buildExportMatrix();
        if (body.length === 0) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'No data to export' });
            return;
        }
        const fmt = (row) => row.map((v, i) => (i >= 3 && v !== '' ? formatNum(v) : v));
        const doc = new jsPDF('p', 'mm', 'a4');
        doc.setFontSize(11);
        doc.text(this.reportTitle(), 14, 10);
        doc.autoTable({
            head: [COLUMN_LABELS],
            body: [...body.map(fmt), ...footer.map(fmt)],
            startY: 15,
            styles: { fontSize: 8, cellPadding: 1.5, halign: 'right' },
            columnStyles: { 0: { halign: 'center' }, 1: { halign: 'left' } },
            headStyles: { fillColor: [244, 176, 176], textColor: 0, halign: 'center' },
            // Vertical separator between every column (left edge of each cell + right edge of the last one)
            didDrawCell: (d) => {
                const { x, y, width, height } = d.cell;
                doc.setDrawColor(150);
                doc.setLineWidth(0.2);
                doc.line(x, y, x, y + height);
                if (d.column.index === COLUMN_LABELS.length - 1) doc.line(x + width, y, x + width, y + height);
            },
            didParseCell: (d) => {
                if (d.section === 'body' && d.row.index >= body.length) d.cell.styles.fontStyle = 'bold';
            },
        });
        doc.save('Itemwise_Sales_Summary_Report.pdf');
    };

    render() {
        if (this.getAccess !== undefined && this.getAccess.viewAccess === false) {
            this.props.history.push('/dashboard');
        }

        const { tableData, fromDate, toDate, searched, Restaurant, btnLoading } = this.state;
        const busy = btnLoading !== null;
        const t = searched ? this.getTotals() : {};
        const right = { textAlign: 'right' };
        const center = { textAlign: 'center' };

        const headerGroup = (
            <ColumnGroup>
                <Row>
                    <Column header={this.reportTitle()} colSpan={4} style={center} />
                </Row>
                <Row>
                    <Column header="S.NO" style={{ width: '80px', ...center }} />
                    <Column header="ITEM NAME" style={{ minWidth: '260px' }} />
                    <Column header="QTY" style={{ minWidth: '100px', ...center }} />
                    <Column header="AMOUNT" style={{ minWidth: '140px', ...center }} />
                </Row>
            </ColumnGroup>
        );

        const footerRow = (label, qty, amount, topLine) => {
            const bold = { fontWeight: 'bold', ...(topLine ? { borderTop: '2px solid #333' } : {}) };
            return (
                <Row>
                    <Column footer="" footerStyle={bold} />
                    <Column footer={label} footerStyle={bold} />
                    <Column footer={qty} footerStyle={{ ...bold, ...right }} />
                    <Column footer={formatNum(amount)} footerStyle={{ ...bold, ...right }} />
                </Row>
            );
        };

        const footerGroup = (
            <ColumnGroup>
                {footerRow('TOTAL', t.qty, t.amount, true)}
                {footerRow('CGST', '', t.cgst)}
                {footerRow('SGST', '', t.sgst)}
                {footerRow('NET AMOUNT', '', t.amount)}
            </ColumnGroup>
        );

        return (
            <div className="p-grid padding">
                {busy && <div className="page-loader"></div>}
                <Toast ref={(el) => this.growl = el} />

                <div className="p-col-12">
                    <div className="card card-w-title" style={{ position: 'relative' }}>
                        <ItemwiseSalesSummaryReportHeader />

                        <div className="p-grid p-align-end">
                            <div className="p-col-12 p-md-3 p-lg-3 p-d-flex p-flex-column p-gap-2">
                                <label className="label">Restaurant</label>
                                <Dropdown
                                    placeholder="Restaurant"
                                    name="Restaurant"
                                    options={RESTAURANTS}
                                    value={Restaurant}
                                    onChange={(e) => this.setState({ Restaurant: e.value })}
                                    filter
                                    autoWidth
                                    className="p-col-12"
                                    {...DROPDOWN_CONFIG}
                                />
                            </div>

                            <div className="p-col-12 p-md-2 p-lg-2 p-d-flex p-flex-column p-gap-2">
                                <label className="label">From Date</label>
                                <Calendar placeholder="From Date" value={fromDate} dateFormat="dd/mm/yy" onChange={(e) => this.setState({ fromDate: e.value })} />
                            </div>

                            <div className="p-col-12 p-md-2 p-lg-2 p-d-flex p-flex-column p-gap-2">
                                <label className="label">To Date</label>
                                <Calendar placeholder="To Date" value={toDate} dateFormat="dd/mm/yy" onChange={(e) => this.setState({ toDate: e.value })} />
                            </div>

                            <div className="p-col-12 p-md-5 p-lg-5 p-d-flex p-ai-end p-gap-2">
                                <Button label="Search" icon="pi pi-search" className="p-button-primary" onClick={this.handleSearch} disabled={busy} />
                                <Button label="Show All" icon="pi pi-list" className="p-button-secondary p-ml-2" onClick={this.handleShowAll} disabled={busy} />
                                <Button label="Clear" icon="pi pi-times" className="p-button-secondary p-ml-2" onClick={this.handleClear} disabled={busy} />
                            </div>

                            <div className="p-col-12 p-d-flex p-jc-end p-gap-2 p-mt-1">
                                <Button label="Export as Excel" icon="pi pi-file-excel" className="p-button-warning" onClick={this.handleExportExcel} disabled={!searched || busy} />
                                <Button label="Export as PDF" icon="pi pi-file-pdf" className="p-button-danger p-ml-2" onClick={this.handleExportPdf} disabled={!searched || busy} />
                            </div>
                        </div>

                        {searched && (
                            <div className="p-col-12" style={{ marginTop: '1em', overflowX: 'auto' }}>
                                <DataTable
                                    value={tableData}
                                    dataKey="id"
                                    headerColumnGroup={headerGroup}
                                    footerColumnGroup={tableData.length > 0 ? footerGroup : null}
                                    scrollable
                                    scrollHeight="500px"
                                    style={{ width: '100%' }}
                                    emptyMessage="No records found"
                                    {...DATATABLE_CONFIG}
                                >
                                    <Column header="S.NO" body={(r, o) => o.rowIndex + 1} style={{ width: '80px', ...center }} />
                                    <Column field="name" header="ITEM NAME" style={{ minWidth: '260px' }} />
                                    <Column field="qty" header="QTY" style={{ minWidth: '100px', ...right }} />
                                    <Column field="amount" header="AMOUNT" body={(r) => formatNum(r.amount)} style={{ minWidth: '140px', ...right }} />
                                </DataTable>
                            </div>
                        )}

                        {!searched && (
                            <div className="p-d-flex p-jc-center p-ai-center" style={{ minHeight: '200px', color: '#aaa', fontSize: '15px' }}>
                                <i className="pi pi-search p-mr-2" />
                                Select a date range and click Search to view the report.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    loggedUserDetail: state.auth.userObject,
});

export default connect(mapStateToProps)(ItemwiseSalesSummaryReport);
